import type { VerifyOrderLineNode } from "~mobile/verify/dashboard/helpers/types";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { findItemByOwnCodeFirst } from "~/helpers/mobile/scan/matchScannedItem";
import { OFFLINE_QUEUE_APP, QUEUE_RETENTION_MINUTES, VERIFY_CONTAINER_MUTATION } from "~/helpers/offline/queueMeta";
import { type QueueEntryDescriptor } from "~/helpers/sw/messageContract";
import { normalizeGuid } from "~/helpers/wsId";
import {
	discardQueueEntry,
	dismissFailedEntry,
	enqueueMutation,
	markQueueReplayFailed,
	markQueueReplaySuccess,
	setQueueSnapshot
} from "~/store/offlineQueueSlice";
import { type RowShippingUnit } from "~fulfill/inventory/stock/helpers/rowShippingUnit";

export const MAX_TRACKED_SUBMITTED_LABELS = 200;
export const MAX_CACHED_VERIFY_ORDERS = 20;

export interface VerifyCachedItem {
	absorbedSkus?: string[];
	absorbedUpcs?: string[];
	itemId: string;
	name: string;
	sku: string;
	unit?: null | RowShippingUnit;
	upc: string;
}

export interface VerifyScan {
	barcode: string;
	itemId: string | null;
	itemName: string | null;
	orderId?: string;
	quantity: number;
	scannedAt: string;
	scannedBy?: string;
	unit?: null | RowShippingUnit;
}

export interface VerifyScanEvent {
	barcode: string;
	itemId: string | null;
	orderId?: string;
	quantity?: number;
	scannedAt: string;
	scannedBy?: string;
}

export interface VerifyContainer {
	containerId: string;
	createdAt: string;
	drop: string;
	finished: boolean;
	label: string;
	lastScanAt: string | null;
	lastSubmittedSignature: string | null;
	orderIds: string[];
	scanLog?: VerifyScanEvent[];
	scans: VerifyScan[];
}

export type VerifyPendingPullStatus = "failed" | "saving" | "waiting";

export const PENDING_PULL_PRUNE_GRACE_MS = 60_000;

export interface PrunePendingPullsPayload {
	inFlightKeys: string[];
	liveKeys: string[];
	now: number;
}

export interface VerifyPendingPullLine {
	grade: number;
	lineItemId: string;
	locationId: string;
	quantity: number;
}

export interface VerifyPendingPull {
	orderId: string;
	orderNumber: string;
	pullAttemptKey: string;
	pulls: VerifyPendingPullLine[];
	savedAt: string;
	signature: string;
	status: VerifyPendingPullStatus;
}

export interface VerifyQueuedContainerSend {
	containerId: string;
	enqueuedAt: number;
	refused?: boolean;
}

export interface VerifyState {
	activeContainerId: string | null;
	cachedItems: VerifyCachedItem[];
	cachedOrderLines?: Record<string, VerifyOrderLineNode[]>;
	containers: Record<string, VerifyContainer>;
	pendingPulls?: Record<string, VerifyPendingPull>;
	queuedContainerSends?: Record<string, VerifyQueuedContainerSend>;
	submittedLabels: string[];
}

const initialState: VerifyState = {
	activeContainerId: null,
	cachedItems: [],
	cachedOrderLines: {},
	containers: {},
	submittedLabels: []
};

export interface VerifyCustomerLine {
	customerSku?: string | null;
	customerUpc?: string | null;
	itemId: string;
}

const isSkuMatch = (sku: string | null | undefined, lowered: string): boolean =>
	Boolean(sku && sku.toLowerCase() === lowered);

const isUpcMatch = (upc: string | null | undefined, trimmed: string): boolean =>
	Boolean(upc && (upc === trimmed || (upc.length > 1 && upc.slice(0, -1) === trimmed)));

export const findScannedItem = <T extends VerifyCachedItem>(
	items: T[],
	barcode: string,
	orderLines: readonly VerifyCustomerLine[] = []
): T | undefined => {
	const trimmed = barcode.trim();
	if (!trimmed) return undefined;
	const lowered = trimmed.toLowerCase();
	const ownMatch = findItemByOwnCodeFirst(items, {
		isScannedSku: (sku) => isSkuMatch(sku, lowered),
		isScannedUpc: (upc) => isUpcMatch(upc, trimmed)
	});
	if (ownMatch) return ownMatch;
	const matchingLines = orderLines.filter(
		(line) => isSkuMatch(line.customerSku, lowered) || isUpcMatch(line.customerUpc, trimmed)
	);
	const itemIds = new Set(matchingLines.map((line) => normalizeGuid(line.itemId)));
	if (itemIds.size !== 1) return undefined;
	return items.find((item) => itemIds.has(normalizeGuid(item.itemId)));
};

export const findContainerByLabel = (
	containers: Record<string, VerifyContainer>,
	label: string
): VerifyContainer | undefined =>
	label ? Object.values(containers).find((container) => container.label === label) : undefined;

const expandScanEvents = (scans: VerifyScan[]): VerifyScanEvent[] =>
	scans.flatMap((scan) =>
		Array.from({ length: scan.quantity }, () => ({
			barcode: scan.barcode,
			itemId: scan.itemId,
			orderId: scan.orderId,
			scannedAt: scan.scannedAt,
			scannedBy: scan.scannedBy
		}))
	);

const isSameScanRow = (
	row: VerifyScan,
	scan: Pick<VerifyScan, "barcode" | "itemId" | "orderId" | "scannedBy">
): boolean =>
	row.barcode === scan.barcode &&
	row.itemId === scan.itemId &&
	row.orderId === scan.orderId &&
	row.scannedBy === scan.scannedBy;

const findLastScanEventIndex = (scanLog: VerifyScanEvent[], orderId: string | undefined): number => {
	if (orderId === undefined) return scanLog.length - 1;
	for (let index = scanLog.length - 1; index >= 0; index -= 1) {
		if (scanLog[index].orderId === orderId) return index;
	}
	return -1;
};

const ensureScanLog = (container: VerifyContainer): VerifyScanEvent[] => {
	if (!container.scanLog) {
		container.scanLog = expandScanEvents(container.scans);
	}
	return container.scanLog;
};

const trackLabel = (state: VerifyState, label: string): void => {
	if (!label) return;
	if (!state.submittedLabels.includes(label)) {
		state.submittedLabels.push(label);
	}
	if (state.submittedLabels.length > MAX_TRACKED_SUBMITTED_LABELS) {
		state.submittedLabels = state.submittedLabels.slice(-MAX_TRACKED_SUBMITTED_LABELS);
	}
};

const QUEUE_RETENTION_MS = QUEUE_RETENTION_MINUTES * 60_000;

const queuedContainerIdOf = (entry: QueueEntryDescriptor): string | undefined =>
	entry.app === OFFLINE_QUEUE_APP.VERIFY && entry.mutationName === VERIFY_CONTAINER_MUTATION
		? entry.reconciliationId
		: undefined;

const toQueuedContainerSend = (
	entry: QueueEntryDescriptor,
	containerId: string,
	now: number
): VerifyQueuedContainerSend => ({ containerId, enqueuedAt: entry.enqueuedAt ?? now });

const wasDroppedUnsent = (send: VerifyQueuedContainerSend, now: number): boolean =>
	Boolean(send.refused) || now - send.enqueuedAt > QUEUE_RETENTION_MS;

const clearSubmittedSignature = (state: VerifyState, containerId: string): void => {
	const container = state.containers[containerId];
	if (container) container.lastSubmittedSignature = null;
};

const releaseUnsentSend = (state: VerifyState, clientId: string): void => {
	const send = state.queuedContainerSends?.[clientId];
	if (!send) return;
	delete state.queuedContainerSends?.[clientId];
	clearSubmittedSignature(state, send.containerId);
};

export const verifySlice = createSlice({
	extraReducers: (builder) => {
		builder.addCase(enqueueMutation, (state, action) => {
			const containerId = queuedContainerIdOf(action.payload);
			if (!containerId) return;
			state.queuedContainerSends ??= {};
			state.queuedContainerSends[action.payload.clientId] = toQueuedContainerSend(
				action.payload,
				containerId,
				Date.now()
			);
		});
		builder.addCase(markQueueReplaySuccess, (state, action) => {
			delete state.queuedContainerSends?.[action.payload.clientId];
		});
		builder.addCase(markQueueReplayFailed, (state, action) => {
			const send = state.queuedContainerSends?.[action.payload.clientId];
			if (send && action.payload.retryable === false) send.refused = true;
		});
		builder.addCase(discardQueueEntry, (state, action) => releaseUnsentSend(state, action.payload));
		builder.addCase(dismissFailedEntry, (state, action) => releaseUnsentSend(state, action.payload));
		builder.addCase(setQueueSnapshot, (state, action) => {
			const now = Date.now();
			state.queuedContainerSends ??= {};
			const sends = state.queuedContainerSends;
			const heldClientIds = new Set<string>();
			for (const entry of action.payload) {
				const containerId = queuedContainerIdOf(entry);
				if (!containerId) continue;
				heldClientIds.add(entry.clientId);
				sends[entry.clientId] ??= toQueuedContainerSend(entry, containerId, now);
			}
			for (const [clientId, send] of Object.entries(sends)) {
				if (heldClientIds.has(clientId)) continue;
				if (wasDroppedUnsent(send, now)) clearSubmittedSignature(state, send.containerId);
				delete sends[clientId];
			}
		});
	},
	initialState,
	name: "verify",
	reducers: {
		clearActiveContainer: (state) => {
			state.activeContainerId = null;
		},
		clearPendingPull: (state, action: PayloadAction<string>) => {
			if (state.pendingPulls) delete state.pendingPulls[action.payload];
		},
		clearFailedPendingPulls: (state, action: PayloadAction<{ orderId: string; signature: string }>) => {
			for (const pending of Object.values(state.pendingPulls ?? {})) {
				if (
					pending.orderId === action.payload.orderId &&
					pending.signature === action.payload.signature &&
					pending.status === "failed"
				)
					delete state.pendingPulls?.[pending.pullAttemptKey];
			}
		},
		markPendingPullWaiting: (state, action: PayloadAction<string>) => {
			const pending = state.pendingPulls?.[action.payload];
			if (pending) pending.status = "waiting";
		},
		markPendingPullFailed: (state, action: PayloadAction<string>) => {
			const pending = state.pendingPulls?.[action.payload];
			if (pending) pending.status = "failed";
		},
		markContainerSubmitLanded: (state, action: PayloadAction<string>) => {
			const landed = state.containers[action.payload];
			if (landed) trackLabel(state, landed.label);
		},
		markContainerSubmitted: (state, action: PayloadAction<{ containerId: string; signature: string | null }>) => {
			const container = state.containers[action.payload.containerId];
			if (container) {
				container.lastSubmittedSignature = action.payload.signature;
			}
		},
		openContainer: (state, action: PayloadAction<{ container: VerifyContainer }>) => {
			const { container } = action.payload;
			if (!state.containers[container.containerId]) {
				state.containers[container.containerId] = container;
			}
			state.activeContainerId = container.containerId;
		},
		recordScan: (state, action: PayloadAction<{ containerId: string; scan: VerifyScan }>) => {
			const { containerId, scan } = action.payload;
			const container = state.containers[containerId];
			if (!container) return;
			const scanLog = ensureScanLog(container);
			const existing = container.scans.find((candidate) => isSameScanRow(candidate, scan));
			if (existing) {
				existing.quantity += scan.quantity;
				existing.unit = scan.unit;
			} else {
				container.scans.push(scan);
			}
			scanLog.push({
				barcode: scan.barcode,
				itemId: scan.itemId,
				orderId: scan.orderId,
				quantity: scan.quantity,
				scannedAt: scan.scannedAt,
				scannedBy: scan.scannedBy
			});
			container.lastScanAt = scan.scannedAt;
		},
		removeContainer: (state, action: PayloadAction<string>) => {
			delete state.containers[action.payload];
			if (state.activeContainerId === action.payload) {
				state.activeContainerId = null;
			}
		},
		replaceContainerScans: (state, action: PayloadAction<{ containerId: string; scans: VerifyScan[] }>) => {
			const container = state.containers[action.payload.containerId];
			if (!container) return;
			container.scans = action.payload.scans;
			container.scanLog = expandScanEvents(action.payload.scans);
			container.lastScanAt = action.payload.scans.length > 0 ? new Date().toISOString() : null;
		},
		setCachedOrderLines: (state, action: PayloadAction<{ orderId: string; lines: VerifyOrderLineNode[] }>) => {
			state.cachedOrderLines ??= {};
			delete state.cachedOrderLines[action.payload.orderId];
			state.cachedOrderLines[action.payload.orderId] = action.payload.lines;
			const oldestOrderIds = Object.keys(state.cachedOrderLines).slice(0, -MAX_CACHED_VERIFY_ORDERS);
			for (const orderId of oldestOrderIds) delete state.cachedOrderLines[orderId];
		},
		setCachedItems: (state, action: PayloadAction<VerifyCachedItem[]>) => {
			state.cachedItems = action.payload;
		},
		prunePendingPulls: (state, action: PayloadAction<PrunePendingPullsPayload>) => {
			const live = new Set([...action.payload.liveKeys, ...action.payload.inFlightKeys]);
			for (const pending of Object.values(state.pendingPulls ?? {})) {
				const isRecent = action.payload.now - Date.parse(pending.savedAt) < PENDING_PULL_PRUNE_GRACE_MS;
				if (pending.status !== "failed" && !isRecent && !live.has(pending.pullAttemptKey))
					delete state.pendingPulls?.[pending.pullAttemptKey];
			}
		},
		stagePendingPull: (state, action: PayloadAction<VerifyPendingPull>) => {
			state.pendingPulls ??= {};
			state.pendingPulls[action.payload.pullAttemptKey] = action.payload;
		},
		setContainerFinished: (state, action: PayloadAction<{ containerId: string; finished: boolean }>) => {
			const container = state.containers[action.payload.containerId];
			if (container) {
				container.finished = action.payload.finished;
			}
		},
		trackSubmittedLabel: (state, action: PayloadAction<string>) => {
			trackLabel(state, action.payload);
		},
		undoLastScan: {
			prepare: (containerId: string, orderId?: string) => ({ payload: { containerId, orderId } }),
			reducer: (state, action: PayloadAction<{ containerId: string; orderId?: string }>) => {
				const container = state.containers[action.payload.containerId];
				if (!container) return;
				const scanLog = ensureScanLog(container);
				const eventIndex = findLastScanEventIndex(scanLog, action.payload.orderId);
				if (eventIndex === -1) return;
				const [lastEvent] = scanLog.splice(eventIndex, 1);
				const rowIndex = container.scans.findIndex((scan) => isSameScanRow(scan, lastEvent));
				if (rowIndex !== -1) {
					const row = container.scans[rowIndex];
					const undoneEaches = lastEvent.quantity ?? 1;
					if (row.quantity > undoneEaches) {
						row.quantity -= undoneEaches;
					} else {
						container.scans.splice(rowIndex, 1);
					}
				}
				const nextLast = scanLog[scanLog.length - 1];
				container.lastScanAt = nextLast ? nextLast.scannedAt : null;
			}
		}
	}
});

export const {
	clearActiveContainer,
	clearFailedPendingPulls,
	clearPendingPull,
	markPendingPullFailed,
	markPendingPullWaiting,
	markContainerSubmitLanded,
	markContainerSubmitted,
	openContainer,
	recordScan,
	removeContainer,
	replaceContainerScans,
	setCachedItems,
	setCachedOrderLines,
	setContainerFinished,
	prunePendingPulls,
	stagePendingPull,
	trackSubmittedLabel,
	undoLastScan
} = verifySlice.actions;

export default verifySlice.reducer;
